import { asArray, asNumber, asOptionalString, asRecord, asString, reportDtoAnomaly } from './common';
import { normalizeSupplierOrder, type SupplierOrderDto } from './supplierOrder';
import type { TransportRouteStatus } from '../types/transport';
import { normalizeTransportAddressList } from '../utils/transportAddressUtils';

/**
 * 2026-08-11 (PZ generowane z transportu): the trimmed transport identity
 * carried by `GET /supplier-orders/pz-transport-candidates` — just enough to
 * render the picker's "Trasa" / "Data załadunku" columns and the plate/carrier
 * a truck is known by. Deliberately non-financial, like every other transport
 * shape surfaced outside the transport module itself.
 */
export interface PZTransportCandidateTransportDto {
  id: string;
  routeNumber: string;
  transportCompany: string;
  licensePlate1: string | null;
  licensePlate2: string | null;
  vehicleRegistration: string | null;
  loadingDate: string | null;
  departureDate: string | null;
  status: TransportRouteStatus;
  originCity: string | null;
  destinationCity: string | null;
  loadingAddresses: string[];
  deliveryAddresses: string[];
}

/**
 * A supplier order (ZD) candidate as it rides along with a transport
 * candidate — the same shape `GET /supplier-orders/pz-candidates` has always
 * returned (supplier, items, pzReceipt eligibility), plus `customerNames`:
 * the customer(s) of any customer order(s) allocated to this ZD, so the
 * picker's "Klient" column can answer "who is this delivery for" without a
 * second round trip. Empty means a stock purchase (no customer allocation).
 */
export interface SupplierOrderPZTransportCandidateDto extends SupplierOrderDto {
  customerNames: string[];
}

/**
 * One row of `GET /supplier-orders/pz-transport-candidates`: a transport that
 * physically carries at least one ZD with a still-receivable line. The picker
 * renders one row per transport (flat for a single ZD, expandable for
 * several) rather than one row per ZD, because the operator's question is
 * "what did this truck bring in", not "which ZD is this".
 */
export interface PZTransportCandidateDto {
  transport: PZTransportCandidateTransportDto;
  supplierOrders: SupplierOrderPZTransportCandidateDto[];
}

function normalizePZTransportCandidateTransport(
  raw: unknown,
  entity: string,
): PZTransportCandidateTransportDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    id: asString(record?.id),
    routeNumber: asString(record?.routeNumber),
    transportCompany: asString(record?.transportCompany),
    licensePlate1: asOptionalString(record?.licensePlate1) ?? null,
    licensePlate2: asOptionalString(record?.licensePlate2) ?? null,
    vehicleRegistration: asOptionalString(record?.vehicleRegistration) ?? null,
    loadingDate: asOptionalString(record?.loadingDate) ?? null,
    departureDate: asOptionalString(record?.departureDate) ?? null,
    // Trimmed shape carries no strict enum guarantee from this endpoint —
    // an unrecognised value still renders (the picker only uses status for a
    // best-effort label), so this stays a plain string cast rather than
    // duplicating the full ROUTE_STATUSES allow-list from dto/transport.ts.
    status: (asOptionalString(record?.status) as TransportRouteStatus | undefined) || 'PLANNED',
    originCity: asOptionalString(record?.originCity) ?? null,
    destinationCity: asOptionalString(record?.destinationCity) ?? null,
    loadingAddresses: normalizeTransportAddressList(record?.loadingAddresses),
    deliveryAddresses: normalizeTransportAddressList(record?.deliveryAddresses),
  };
}

function normalizeSupplierOrderPZTransportCandidate(
  raw: unknown,
  entity: string,
): SupplierOrderPZTransportCandidateDto {
  const record = asRecord(raw);
  const supplierOrder = normalizeSupplierOrder(raw, entity);

  return {
    ...supplierOrder,
    customerNames: asArray(record?.customerNames, (name) => asString(name)).filter(Boolean),
  };
}

export function normalizePZTransportCandidates(
  raw: unknown,
  entity = 'pzTransportCandidates',
): PZTransportCandidateDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    return {
      transport: normalizePZTransportCandidateTransport(record?.transport, `${entity}[${index}].transport`),
      supplierOrders: asArray(record?.supplierOrders, (supplierOrder, supplierOrderIndex) => (
        normalizeSupplierOrderPZTransportCandidate(
          supplierOrder,
          `${entity}[${index}].supplierOrders[${supplierOrderIndex}]`,
        )
      )),
    };
  });
}

/**
 * `GET /supplier-orders/:id/pz-documents` — the PZ documents already issued
 * off a supplier order, for the read-only "Dokumenty PZ" card on the ZD's
 * Powiązania tab.
 */
export interface SupplierOrderPZDocumentSummaryDto {
  id: string;
  pzNumber: string;
  status: string;
  receiptDate: string | null;
  itemCount: number;
}

export function normalizeSupplierOrderPZDocuments(
  raw: unknown,
  entity = 'supplierOrderPZDocuments',
): SupplierOrderPZDocumentSummaryDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    return {
      id: asString(record?.id),
      pzNumber: asString(record?.pzNumber),
      status: asString(record?.status),
      receiptDate: asOptionalString(record?.receiptDate) ?? null,
      itemCount: asNumber(record?.itemCount),
    };
  }).filter((document) => Boolean(document.id));
}
